import { defineStore } from 'pinia'

import { apiClient } from "@/helpers/api.js"
import { handleApiError } from '@/services/errorHandler'
import { useBatchStore } from '@/stores/BatchStore'
import { isSearchInventoryEnabled } from '@/helpers/featureFlags'

export const useSearchStore = defineStore('SearchStore', {
  state: () => {
    return {
      selectedFilter: '',
      searchState: 'start',

      batchesSearched: [],
      recentlyModifiedBatches: [],
      cdp_search: {
        open: false,
        state: '',
        locationOfSearchCall: ''
      },

      loadingBatches: false,
      loadingMatches: false,
      loadingMoreMatches: false,

      hoveredOverMatchCard: null,

      matchesData: {
        matches: []
      },

      /**
       * Optional `inventory` block on the /search response (CDP-2508) — the
       * user's OWN matching cards, from Typesense. Present only when Typesense
       * is enabled for the user; the key is simply absent otherwise, so this
       * stays an empty list and every consumer can read it unconditionally.
       */
      inventoryData: {
        items: [],
        totalHits: 0,
        page: 1,
        totalPages: 0,
      },

      // Shared seed so every DatabaseMatchField instance for the selected card
      // (regular + pinned copy) stays in sync when a match is removed: they all
      // pre-fill the search input with the removed match title. Keyed by cardId
      // so a stale seed from another card can't leak in.
      databaseMatchSeed: { cardId: null, term: '' },

      triggerOpenSearchResult: false
    }
  },

  actions: {
    updateSearchStateBasedOnDataObject(data) {
      Object.entries(data).forEach(([key, value]) => {
        if (typeof value === 'object') {
          Object.entries(data).forEach(([key, value]) => {
            this[key] = value
          })
        } else {
          this[key] = value
        }
      })
    },
    async searchUserBatches(options = {}) {
      try {
        const { query, kind } = options

        this.loadingBatches = true

        const params = {}

        if (query) {
          params.q = query
        }

        if (kind) {
          params.kind = kind
        }

        const { data } = await apiClient().get(`/batches`, query || kind ? {
          params
        } : {})

        if (!query) {
          this.recentlyModifiedBatches = data.items.sort((a, b) => {
            const dateA = new Date(a.updated_at)
            const dateB = new Date(b.updated_at)
            return dateB - dateA; // Sorting in descending order
          }).slice(0, 5)
        } else {
          this.recentlyModifiedBatches = []
        }
        this.batchesSearched = data.items
        this.loadingBatches = false
      } catch (error) {
        handleApiError(error, { component: 'SearchStore', action: 'searchUserBatches' })
        this.loadingBatches = false
      }
    },

    /**
     * Fold the response's optional `inventory` block into state.
     *
     * Absent (Typesense off for this user) resets to empty rather than keeping
     * the previous query's hits around — a stale "your inventory" list under a
     * new search would read as a result for THIS query.
     */
    setInventoryData(inventory, page = 1) {
      // Pilot gate (CDP-2508). Dropping the block HERE is what makes every other
      // user's search behave exactly as before: no hits reach the UI, so no
      // inventory section renders and Card Database rows keep "Add Card".
      if (!isSearchInventoryEnabled() || !inventory?.items?.length) {
        if (page === 1) this.inventoryData = { items: [], totalHits: 0, page: 1, totalPages: 0 }
        return
      }

      this.inventoryData = {
        ...inventory,
        items: page > 1 ? [...this.inventoryData.items, ...inventory.items] : [...inventory.items],
      }
    },

    async searchDatabaseMatches(options = {}, skipLoaderUpdate = false) {
      const { query, page = 1, kind } = options
      const batchStore = useBatchStore() 
      try {
        if (!query && this.selectedFilter === 'Card Database' && batchStore.selectedBatchDetailsViewOption === 'Inspector') {
          this.matchesData = {
            matches: []
          }
          return 
        }

        this.loadingMoreMatches = true

        if (!skipLoaderUpdate) {
          this.loadingMatches = true
        }
       
        const params = {
          page
        }

        if (query) {
          params.q = query
        }

        if (kind) {
          params.kind = kind
        }

        if (options.baseSetIds) {
          params.baseSetIds = options.baseSetIds
        }

        if (options.setIds) {
          params.setIds = options.setIds
        }

        // CDP-2508: `/search` moves to the main Laravel API (`apiClient()`) so
        // the response can carry the optional `inventory` block — but only for
        // the pilot account. Everyone else keeps hitting the batch processor
        // (`apiClient(true)`), i.e. byte-for-byte the previous request, so the
        // endpoint switch can be validated on one account before it ships.
        // Params are identical either way.
        const onLaravelSearch = isSearchInventoryEnabled()

        const { data } = await apiClient(!onLaravelSearch).get(`/search`, query || kind ? {
          params
        } : {})

        if (page > 1) {
          this.matchesData = { ...data, matches: [...this.matchesData.matches, ...data.matches] }
        } else {
          this.matchesData = { ...data, matches: data.matches ? [...data.matches] : [] }
        }

        this.setInventoryData(data.inventory, page)

        this.loadingMatches = false
        this.loadingMoreMatches = false
      } catch (error) {
        this.loadingMatches = false
        this.loadingMoreMatches = false
        handleApiError(error, { component: 'SearchStore', action: 'searchDatabaseMatches' })
      }
    },

  }
})